import { Employer, SearchResult } from '@backrs_ui/common-ui';

import { SEARCH_CATEGORIES } from '@backrs_ui/grow-ui/src/pages/Search/search.filters';
import { SearchCategory } from '@backrs_ui/grow-ui/src/pages/Search/search.types';

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';
import { selectIsSharedSessionActive } from '../sharedSession/sharedSession';
import type { RootState } from '../store';

export const employerApi = createApi({
  reducerPath: 'employerApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getEmployerBySlug: build.query<Employer, string>({
      query: (slug) => `employers/by-slug/${slug}`,
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getEmployersByName: build.query<Employer[], string[]>({
      query: (names) => `employers/by-names?names=${names.join(',')}`,
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    getEmployerById: build.query<Employer, string>({
      queryFn: async (id, queryApi, _extraOptions, baseQuery): Promise<{ data: Employer }> => {
        const state = queryApi.getState() as RootState;
        const isSharedSession: boolean = selectIsSharedSessionActive(state);

        const url: string = isSharedSession
          ? `shared/solution-session/employers/${id}`
          : `employers/${id}`;

        return baseQuery({ url }) as Promise<{ data: Employer }>;
      },
      keepUnusedDataFor: 60 * 60 * 24,
    }),
    searchForEmployerResources: build.query<
      SearchResult,
      {
        types?: SearchCategory[];
        sponsor_id?: number;
        employer_id?: number;
        also_match_sponsor_and_validator?: boolean;
        perPage?: number;
        page?: number;
      }
    >({
      query: ({
        types,
        sponsor_id,
        employer_id,
        also_match_sponsor_and_validator,
        page = 1,
        perPage = 12,
      }) => {
        const filter = JSON.stringify({
          q: '', // empty query since we fetch all resources associated with the employer
          ...((types?.length || 0) > 0 && { types }),
          ...(sponsor_id && { sponsor_id }),
          ...(employer_id && { employer_id }),
          ...(also_match_sponsor_and_validator && { also_match_sponsor_and_validator: true }),
        });

        const searchParams = new URLSearchParams();

        searchParams.append('filter', filter);

        searchParams.append('page', String(page));
        searchParams.append('perPage', String(perPage));

        return `content-search?${searchParams.toString()}`;
      },
    }),
    searchForEmployerPeople: build.query<
      SearchResult,
      {
        employerName?: string;
        perPage?: number;
        page?: number;
      }
    >({
      query: ({ employerName, page = 1, perPage = 12 }) => {
        const filter = JSON.stringify({
          q: '',
          types: [SEARCH_CATEGORIES.PEOPLE_INTERNAL],
          ...(employerName && { employerName }),
        });

        const searchParams = new URLSearchParams();

        searchParams.append('filter', filter);
        searchParams.append('page', String(page));
        searchParams.append('perPage', String(perPage));

        return `content-search?${searchParams.toString()}`;
      },
    }),
  }),
});

export const {
  useGetEmployerBySlugQuery,
  useGetEmployersByNameQuery,
  useGetEmployerByIdQuery,
  useSearchForEmployerResourcesQuery,
  useSearchForEmployerPeopleQuery,
  usePrefetch: usePrefetchEmployer,
  util: { resetApiState },
} = employerApi;
